import type {
  CalculateEstimateRequest,
  CalculateEstimateResponse,
} from "src/billinglatest/clients/service.client";
import type { PixiAddress } from "../../../hooks/usePixiWizardState";
import type { CheckoutPricing } from "../useCheckoutPricing";
import { pixiAddressToBillingAddress } from "./checkoutPayload";

/**
 * A tax line ready for display: the provider's label and the amount in MINOR
 * units (cents), matching `util.currency.formatted`. Kept separate from the raw
 * client shape so the Order Summary never depends on the estimate wire format.
 */
export interface TaxLine {
  label: string;
  amount: number;
}

export interface BuildEstimateRequestArgs {
  pricing: CheckoutPricing;
  billingAddress: PixiAddress;
  shippingAddress: PixiAddress;
  vatNumber?: string;
}

/**
 * Build the checkout estimate request from the resolved pricing + collected
 * address/VAT. The free-device deal estimates the PIXI bundle (`option: "pixi"`
 * + shipping) for parity with the `start` conversion; the discount deal (no
 * hardware) estimates the plan itself via `planId` + `quantity`. Both carry the
 * billing address + VAT number so Chargebee can resolve the tax (GRWBKK-1744).
 */
export const buildEstimateRequest = ({
  pricing,
  billingAddress,
  shippingAddress,
  vatNumber,
}: BuildEstimateRequestArgs): CalculateEstimateRequest => {
  // planId + quantity are always sent — the backend reads them for BOTH the
  // generic estimate and the PIXI bundle branch (it fetches the plan by id and
  // assembles the bundle at this quantity). Same id shape the purchase charges
  // (`group-cycle-currency`), so the estimated tax base matches the real charge.
  const base: CalculateEstimateRequest = {
    planId: `${pricing.planId}-${pricing.billingCycle}-${pricing.currency.toLowerCase()}`,
    quantity: pricing.licenses,
    billingAddress: pixiAddressToBillingAddress(billingAddress),
    vatNumber: vatNumber?.trim() || undefined,
  };

  // The free-device deal adds the PIXI bundle context (device + shipping) for
  // parity with `start`; the discount deal (no hardware) estimates the plan
  // alone. Without a resolved shipping service there's no bundle to quote.
  if (pricing.isFreeDevice && pricing.shippingService) {
    return {
      ...base,
      option: "pixi",
      shippingService: pricing.shippingService,
      shippingAddress: pixiAddressToBillingAddress(shippingAddress),
    };
  }

  return base;
};

/**
 * Map the estimate response to displayable tax lines. The provider description
 * is the label (never hardcode a rate); `amount` stays in cents. An absent or
 * empty `taxes` array yields no lines — a non-taxable location or a valid EU VAT
 * reverse-charge, where the Order Summary shows no tax row.
 */
export const mapEstimateTaxes = (
  response?: CalculateEstimateResponse,
): TaxLine[] =>
  (response?.taxes ?? []).map((tax) => ({
    label: tax.description || tax.name,
    amount: tax.amount,
  }));

/**
 * The estimate's tax-inclusive total (licence + shipping + tax) in cents, or
 * `undefined` when there's no estimate yet — the caller then falls back to the
 * client-computed total so the Order Summary always shows an amount.
 */
export const estimateTotalCents = (
  response?: CalculateEstimateResponse,
): number | undefined => response?.total;
